import React from 'react';
import styles from './StudyCard.scss';
import classnames from 'classnames';
import lodash from 'lodash';
import moment from 'moment';
import LifecycleIndicator from 'components/common/Study/LifecycleIndicator';
import { STUDY_USER_TYPE, STUDY_TEAM_DESC } from 'components/common/Study/StudyTeamMemberForm';
import { Button, BUTTON } from 'components/common/Button/Button';
import icons from 'styles/icons/icons.scss';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import { Conditional } from 'common/components/layout';

const { SIZE, COLOR, BGCOLOR } = BUTTON;

export function TeamMembers({
    onClear,
    onEdit,
    studyTeam,
    setSelectedUser,
    setShowModal,
    setShowPanel,
    doaStudyDelegations = [],
    isDoaEnabled,
    canManageStudyUsers,
    canManageStudyAdmins,
    fromCreateStudyWorkflow = false,
    doaStudyTasks = [],
}) {
    function editUser(teamMember) {
        if (onEdit) {
            onEdit(teamMember);
        } else {
            setSelectedUser(teamMember);
            setShowPanel(true);
        }
    }

    function clearUser(teamMember) {
        if (onClear) {
            onClear(teamMember);
        } else {
            setSelectedUser(teamMember);
            setShowModal(true);
        }
    }

    const hasDelegation = (userId) => {
        return doaStudyDelegations.some((delegation) => delegation.user.id === userId);
    };

    const hasSetupStudyTask = () => {
        return doaStudyTasks.length !== 0;
    };

    const countTotalAssignedTasks = (teamMember) => {
        const userID = teamMember.userId;
        const sortedDelegations = lodash.orderBy(doaStudyDelegations, (doa) => moment(doa.createdAt), ['desc']);
        const delegation = sortedDelegations.find(
            (doa) => doa.user.id === userID && doa.rolesManagement && doa.rolesManagement.id === teamMember.studyRoleId
        );
        if (delegation) {
            return delegation.taskIds.length;
        }
        return 0;
    };

    const canTeamMemberPerformAction = (teamMember) => {
        return (teamMember.hasAdminRole && canManageStudyAdmins) || (!teamMember.hasAdminRole && canManageStudyUsers);
    };

    const isEditable = (teamMember) => {
        if (fromCreateStudyWorkflow) return true;
        switch (teamMember.studyUserType) {
            case STUDY_USER_TYPE.ADMIN:
                return canManageStudyAdmins;
            case STUDY_USER_TYPE.TEAM:
                return canTeamMemberPerformAction(teamMember);
            case STUDY_USER_TYPE.VIEWER:
                return canManageStudyUsers;
            default:
                return false;
        }
    };

    const isRemovable = (teamMember) => {
        if (fromCreateStudyWorkflow) return true;
        switch (teamMember.studyUserType) {
            case STUDY_USER_TYPE.ADMIN:
                return canManageStudyAdmins;
            case STUDY_USER_TYPE.TEAM:
                return canTeamMemberPerformAction(teamMember);
            case STUDY_USER_TYPE.VIEWER:
                return canManageStudyUsers;
            default:
                return false;
        }
    };

    const shouldShowPill = (teamMember) => {
        return (
            teamMember.studyUserType === STUDY_USER_TYPE.VIEWER ||
            teamMember.studyUserType === STUDY_USER_TYPE.ADMIN ||
            teamMember.hasAdminRole
        );
    };

    const memberCards = studyTeam.map((teamMember) => {
        return (
            <li key={teamMember.id} className={styles.memberCard} data-cy={`StudyTeamMemberCard-${teamMember.id}`}>
                <TeamMemberDetails
                    teamMember={teamMember}
                    totalAssignedTasks={countTotalAssignedTasks(teamMember)}
                    isDoaEnabled={isDoaEnabled}
                    hasDelegation={hasDelegation(teamMember.userId)}
                    hasSetupStudyTask={hasSetupStudyTask()}
                />
                <TeamMemberControls
                    onClear={clearUser}
                    onEdit={editUser}
                    teamMember={teamMember}
                    isEditable={isEditable(teamMember)}
                    isRemovable={isRemovable(teamMember)}
                    shouldShowPill={shouldShowPill(teamMember)}
                />
            </li>
        );
    });
    return (
        <ul className={styles.memberCards} data-cy="StudyTeamMemberCard">
            {memberCards}
        </ul>
    );
}

const ZeroTasksTooltip = withStyles({
    tooltip: {
        display: 'inline-block',
        opacity: '0.85',
        color: 'white',
        backgroundColor: '#28282E',
        padding: '5px',
        fontSize: 14,
    },
})(Tooltip);

export function TeamMemberDetails({ teamMember, totalAssignedTasks, isDoaEnabled, hasDelegation, hasSetupStudyTask }) {
    const roleName = lodash.get(teamMember, 'studyRole.name', null);
    const hasRole = roleName != null;
    const isDelegationAvailable = isDoaEnabled && hasSetupStudyTask && hasDelegation && hasRole;
    const delegatedUserMissingTasks = isDelegationAvailable && totalAssignedTasks === 0;
    return (
        <div className={styles.memberCardDetails} data-cy="TeamMemberDetails">
            <p
                className={classnames({
                    [styles.memberCardWithRole]: hasRole,
                    [styles.memberCardRole]: !hasRole,
                })}
                data-cy="TeamMemberDetails-p"
            >
                {hasRole && roleName}
            </p>
            {delegatedUserMissingTasks && (
                <ZeroTasksTooltip
                    title="Please setup tasks for this user so their delegation can be signed."
                    placement="bottom-start"
                    interactive
                >
                    <span className={`${icons.materialIcons} ${styles.doaZeroTaskIcon}`}>error_outline</span>
                </ZeroTasksTooltip>
            )}

            <div className={styles.memberWithAssignedTasks}>
                <h1 data-cy="TeamMemberDetails-displayName">{teamMember.displayName}</h1>
                {isDoaEnabled && hasDelegation && hasRole && isDelegationAvailable && (
                    <span
                        className={totalAssignedTasks === 0 ? styles.doaZeroTaskError : styles.doaHasTasks}
                        data-cy="TeamMemberDetails-span"
                    >
                        {`${totalAssignedTasks} ${totalAssignedTasks === 1 ? 'Task' : 'Tasks'} Assigned`}
                    </span>
                )}
            </div>
        </div>
    );
}

export function TeamMemberControls({ onClear, onEdit, teamMember, isEditable, isRemovable, shouldShowPill }) {
    const EditDetailsButton = () => (
        <Button
            className={styles.memberCardEdit}
            text={'Edit Details'}
            type={SIZE.PRIMARY}
            color={COLOR.BLUE}
            bgcolor={BGCOLOR.CLEAR}
            onClick={() => onEdit(teamMember)}
            data-cy="TeamMemberDetails-Button"
        />
    );

    const CloseButton = () => (
        <i className={styles.memberCardDelete} onClick={() => onClear(teamMember)} data-cy="TeamMemberDetails-onClick">
            {'close'}
        </i>
    );

    const getPillTitle = (type) => {
        switch (type) {
            case STUDY_USER_TYPE.VIEWER:
                return STUDY_TEAM_DESC.VIEWER;
            default:
                return STUDY_TEAM_DESC.ADMIN;
        }
    };

    const getLifecycleIndicator = (type) => {
        switch (type) {
            case STUDY_USER_TYPE.VIEWER:
                return styles.lifecycleIndicator;
            default:
                return styles.adminLifecycleIndicator;
        }
    };

    const Pill = ({ type }) => (
        <LifecycleIndicator
            title={getPillTitle(type)}
            withBackground={true}
            className={getLifecycleIndicator(type)}
            data-cy="TeamMemberDetails-Pill"
        />
    );

    return (
        <div className={styles.memberCardControls} data-cy="TeamMemberDetails-return">
            <Conditional condition={shouldShowPill}>
                <Pill type={teamMember.studyUserType} />
            </Conditional>
            <Conditional condition={isEditable}>
                <EditDetailsButton />
            </Conditional>
            <Conditional condition={isRemovable}>
                <CloseButton />
            </Conditional>
        </div>
    );
}
